import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Database, Download, HardDriveDownload, Loader2, RefreshCw,
  ShieldCheck, Upload,
} from "lucide-react";
import { API, api, apiError, logError, shortDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const kb = (n) => `${Math.round((Number(n) || 0) / 1024).toLocaleString("en-IN")} KB`;

function statusBadge(status) {
  const map = {
    healthy: { variant: "default", label: "Healthy" },
    unverified: { variant: "secondary", label: "Unverified" },
    stale: { variant: "destructive", label: "Backup overdue" },
    missing: { variant: "destructive", label: "No backup yet" },
  };
  return map[status] || map.missing;
}

export default function DataVault() {
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [restores, setRestores] = useState([]);
  const [imports, setImports] = useState([]);
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const restoreEntries = useMemo(
    () => Object.entries(restoreTarget?.report || {}).filter(([, v]) => v.in_backup || v.currently),
    [restoreTarget]
  );
  const [exportEntity, setExportEntity] = useState("customers");
  const [importEntity, setImportEntity] = useState("customers");
  const [preview, setPreview] = useState(null);
  const [folders, setFolders] = useState([]);
  const [openedFolder, setOpenedFolder] = useState("");
  const [folderFiles, setFolderFiles] = useState([]);

  const openFolder = async (folder) => {
    setOpenedFolder(folder);
    try {
      const { data } = await api.get(`/vault?folder=${encodeURIComponent(folder)}`);
      setFolderFiles(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, b, r, i, sec, fo] = await Promise.all([
        api.get("/vault/summary"),
        api.get("/backups/health"),
        api.get("/backups"),
        api.get("/restore-history"),
        api.get("/imports"),
        api.get("/security/overview"),
        api.get("/files-folders"),
      ]);
      setFolders(fo.data);      setSummary(s.data);
      setHealth(h.data);
      setBackups(b.data);
      setRestores(r.data);
      setImports(i.data);
      setSecurity(sec.data);
    } catch (e) {
      logError("data vault", e);
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      return res;
    } catch (e) {
      toast.error(apiError(e));
      return null;
    } finally {
      setBusy("");
    }
  };

  const uploadPreview = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setBusy("import");
    try {
      const { data } = await api.post(`/imports/preview?entity=${importEntity}`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (loading || !health) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const badge = statusBadge(health.status);

  return (
    <div data-testid="data-vault">
      <PageHeader
        testId="vault-header"
        title="Data Vault"
        subtitle="Owner-only backups, verified restore, exports, imports and security activity"
        actions={
          <>
            <Button variant="outline" onClick={load} className="gap-1.5" data-testid="vault-refresh-button">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
            <Button
              className="gap-1.5"
              disabled={busy === "backup"}
              data-testid="create-backup-button"
              onClick={() => run("backup", () => api.post("/backups"), "Backup created and verified")}
            >
              {busy === "backup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              Back up now
            </Button>
          </>
        }
      />

      <div className="p-4 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="vault-stats">
          {[
            ["Backup status", badge.label, health.last_backup ? `last ${shortDate(health.last_backup.created_at)}` : "run one now"],
            ["Verified backups", `${health.verified_backups}/${health.total_backups}`, `retention ${health.retention_days} days`],
            ["Records protected", (health.last_backup?.total_records || 0).toLocaleString("en-IN"), health.last_backup ? kb(health.last_backup.size_bytes) : "—"],
            ["Restore test", health.last_backup?.restore_tested_at ? shortDate(health.last_backup.restore_tested_at) : "not tested", `${summary?.restores || 0} restores done`],
          ].map(([label, value, hint]) => (
            <div key={label} className="surface p-4">
              <div className="overline">{label}</div>
              <div className="mt-1 font-display text-2xl font-semibold">{value}</div>
              <div className="text-xs text-muted-foreground">{hint}</div>
            </div>
          ))}
        </div>

        {health.status !== "healthy" && (
          <div
            className="mt-4 flex items-start gap-2 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
            data-testid="vault-warning"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
            <span>
              {health.status === "missing"
                ? "No backup exists yet. Create one now — your live business data is unprotected."
                : "The latest backup is older than expected or unverified. Run a fresh backup."}
            </span>
          </div>
        )}

        <Tabs defaultValue="backups" className="mt-5">
          <TabsList data-testid="vault-tabs">
            <TabsTrigger value="backups" data-testid="vault-tab-backups">Backups</TabsTrigger>
            <TabsTrigger value="export" data-testid="vault-tab-export">Export</TabsTrigger>
            <TabsTrigger value="import" data-testid="vault-tab-import">Import</TabsTrigger>
            <TabsTrigger value="documents" data-testid="vault-tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="security" data-testid="vault-tab-security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="documents">
            <div className="surface p-4" data-testid="vault-folders">
              <p className="mb-3 text-sm text-muted-foreground">
                Every uploaded document is filed in its own folder — Leads, Stock Bills, Expenses,
                Purchases and more.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {folders.map((f) => (
                  <button
                    key={f.folder}
                    type="button"
                    data-testid={`vault-folder-${f.folder.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => openFolder(f.folder)}
                    className={`rounded border p-3 text-left transition-colors hover:bg-accent ${
                      openedFolder === f.folder ? "border-primary" : "border-border"
                    }`}
                  >
                    <div className="text-sm font-medium">{f.folder}</div>
                    <div className="text-xs text-muted-foreground">
                      {f.files} file(s) • {kb(f.bytes)}
                    </div>
                  </button>
                ))}
                {folders.length === 0 && (
                  <div className="text-sm text-muted-foreground">No documents uploaded yet.</div>
                )}
              </div>
              {openedFolder && (
                <div className="mt-4" data-testid="vault-folder-files">
                  <div className="overline mb-1">{openedFolder}</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File</TableHead>
                        <TableHead>Uploaded by</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Size</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {folderFiles.map((f) => (
                        <TableRow key={f.id}>
                          <TableCell>
                            <a className="underline" target="_blank" rel="noreferrer"
                              href={`${API}${f.url}`} data-testid={`vault-file-${f.id}`}>
                              {f.filename}
                            </a>
                          </TableCell>
                          <TableCell>{f.uploaded_by_name || "-"}</TableCell>
                          <TableCell>{shortDate(f.created_at)}</TableCell>
                          <TableCell className="text-right">{kb(f.size)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="backups">
            <div className="surface overflow-x-auto" data-testid="backups-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Backup</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((b) => (
                    <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                      <TableCell className="font-mono-num text-xs">
                        {b.filename}
                        <div className="text-[10px] text-muted-foreground">
                          {String(b.created_at).slice(0, 16).replace("T", " ")} • {b.created_by}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs capitalize">{String(b.kind).replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm">
                        {(b.total_records || 0).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-xs">{kb(b.size_bytes)}</TableCell>
                      <TableCell>
                        {b.verified ? (
                          <Badge className="gap-1 text-[10px]">
                            <CheckCircle2 className="h-3 w-3" /> Verified
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px]">Unverified</Badge>
                        )}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`backup-verify-button-${b.id}`}
                          onClick={() => run(`v-${b.id}`, () => api.post(`/backups/${b.id}/verify`), "Backup verified")}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`backup-download-button-${b.id}`}
                          onClick={() => window.open(`${API}/backups/${b.id}/download`, "_blank")}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          data-testid={`backup-restore-button-${b.id}`}
                          onClick={async () => {
                            const res = await run(`t-${b.id}`, () =>
                              api.post(`/backups/${b.id}/restore`, { dry_run: true }));
                            if (res) {
                              setRestoreTarget({ backup: b, report: res.data.report });
                              setRestoreConfirm("");
                            }
                          }}
                        >
                          <HardDriveDownload className="h-3.5 w-3.5" /> Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {restores.length > 0 && (
              <div className="surface mt-4 p-4" data-testid="restore-history">
                <div className="overline mb-2">Restore history</div>
                {restores.map((r) => (
                  <div key={r.id} className="border-b border-border py-2 text-xs last:border-0">
                    {r.filename} • {(r.total_records || 0).toLocaleString("en-IN")} records •{" "}
                    {r.created_by} • {String(r.created_at).slice(0, 16).replace("T", " ")}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="export">
            <div className="surface p-5">
              <div className="overline mb-3">Export business data</div>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Dataset">
                  <Select value={exportEntity} onValueChange={setExportEntity}>
                    <SelectTrigger className="w-56" data-testid="export-entity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(summary?.exportable || []).map((e) => (
                        <SelectItem key={e} value={e}>{e.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Button
                  className="gap-1.5"
                  data-testid="export-csv-button"
                  onClick={() => window.open(`${API}/exports/${exportEntity}?fmt=csv`, "_blank")}
                >
                  <Download className="h-4 w-4" /> CSV
                </Button>
                <Button
                  variant="outline"
                  className="gap-1.5"
                  data-testid="export-json-button"
                  onClick={() => window.open(`${API}/exports/${exportEntity}?fmt=json`, "_blank")}
                >
                  <Download className="h-4 w-4" /> JSON
                </Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Every export is watermarked and written to the audit log with your name, time and
                record count. Employees cannot export unless you grant that module's export right.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="import">
            <div className="surface p-5">
              <div className="overline mb-3">Import records</div>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Dataset">
                  <Select value={importEntity} onValueChange={setImportEntity}>
                    <SelectTrigger className="w-56" data-testid="import-entity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(summary?.importable || []).map((e) => (
                        <SelectItem key={e} value={e}>{e.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Upload className="h-4 w-4" /> Choose CSV or JSON file
                  <input
                    type="file"
                    hidden
                    accept=".csv,.json"
                    data-testid="import-file-input"
                    onChange={(e) => uploadPreview(e.target.files?.[0])}
                  />
                </label>
                {busy === "import" && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>

              {preview && (
                <div className="mt-4 rounded border border-border p-3 text-sm" data-testid="import-preview">
                  <div className="flex flex-wrap gap-4 text-xs">
                    <span>Total rows: <b>{preview.total}</b></span>
                    <span>Ready: <b data-testid="import-ready-count">{preview.ready_count}</b></span>
                    <span>Duplicates skipped: <b>{preview.duplicates.length}</b></span>
                    <span>Errors: <b>{preview.errors.length}</b></span>
                  </div>
                  {preview.errors.slice(0, 5).map((er) => (
                    <div key={`err-${er.row}`} className="mt-1 text-xs text-destructive">
                      Row {er.row}: {er.reason}
                    </div>
                  ))}
                  {preview.duplicates.slice(0, 5).map((d) => (
                    <div key={`dup-${d.row}`} className="mt-1 text-xs text-muted-foreground">
                      Row {d.row} already exists — will be skipped
                    </div>
                  ))}
                  <Button
                    className="mt-3"
                    disabled={!preview.ready_count || busy === "commit"}
                    data-testid="import-commit-button"
                    onClick={async () => {
                      const res = await run("commit", () =>
                        api.post("/imports/commit", {
                          entity: preview.entity, rows: preview.ready, filename: preview.filename,
                        }), "Records imported");
                      if (res) setPreview(null);
                    }}
                  >
                    Import {preview.ready_count} records
                  </Button>
                </div>
              )}

              {imports.length > 0 && (
                <div className="mt-5" data-testid="import-history">
                  <div className="overline mb-2">Import history</div>
                  {imports.map((im) => (
                    <div key={im.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 text-xs last:border-0">
                      <span>
                        {im.entity} • {im.records} records • {im.created_by} •{" "}
                        {String(im.created_at).slice(0, 16).replace("T", " ")}
                        {im.rolled_back && (
                          <Badge variant="outline" className="ml-2 text-[9px]">rolled back</Badge>
                        )}
                      </span>
                      {!im.rolled_back && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`import-rollback-button-${im.batch}`}
                          onClick={() => run(`rb-${im.batch}`, () =>
                            api.post(`/imports/${im.batch}/rollback`), "Import rolled back")}
                        >
                          Undo import
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="security">
            <div className="grid gap-4 lg:grid-cols-2" data-testid="security-center">
              <div className="surface p-5">
                <div className="overline mb-2">Users</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Total</span><span>{security?.users.total}</span>
                  <span className="text-muted-foreground">Active</span><span>{security?.users.active}</span>
                  <span className="text-muted-foreground">Owners</span><span>{security?.users.owners}</span>
                  <span className="text-muted-foreground">Password not set</span>
                  <span>{security?.users.without_password}</span>
                </div>
              </div>
              <div className="surface p-5">
                <div className="overline mb-2">Suspicious activity</div>
                {(security?.suspicious || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nothing unusual detected.</p>
                ) : (
                  security.suspicious.map((s, i) => (
                    <div key={`${s.type}-${i}`} className="border-b border-border py-1.5 text-xs last:border-0">
                      {String(s.type).replace(/_/g, " ")} — {s.identifier || s.user} ({s.attempts || s.records})
                    </div>
                  ))
                )}
              </div>
              {[["Export activity", security?.export_activity, (d) => `${d.user_name} exported ${d.records} ${d.entity}`],
                ["Database operations", security?.database_operations, (d) => `${d.user_name} • ${String(d.action).replace(/_/g, " ")}`],
                ["Failed sign-ins", security?.failed_logins, (d) => `${d.identifier} — ${d.count} attempts`],
                ["Permission & password changes", security?.permission_changes, (d) => `${d.user_name} • ${String(d.action).replace(/_/g, " ")}`]].map(([title, rows, fmt]) => (
                <div key={title} className="surface p-5">
                  <div className="overline mb-2">{title}</div>
                  {(rows || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No records yet.</p>
                  ) : (
                    rows.slice(0, 8).map((d) => (
                      <div key={d.id || `${d.identifier}-${d.created_at}`} className="border-b border-border py-1.5 text-xs last:border-0">
                        {fmt(d)}
                        <span className="ml-2 text-muted-foreground">
                          {String(d.created_at || d.last_attempt || "").slice(0, 16).replace("T", " ")}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle>Restore from backup</DialogTitle>
            <DialogDescription>
              The backup passed its integrity check. A safety backup of today's data is taken
              automatically before anything is replaced.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-52 overflow-y-auto rounded border border-border p-3 text-xs">
            {restoreEntries.map(([name, v]) => (
              <div key={name} className="flex justify-between py-0.5">
                <span className="text-muted-foreground">{name}</span>
                <span className="font-mono-num">
                  now {v.currently} → after restore {v.in_backup}
                </span>
              </div>
            ))}
          </div>
          <Field label="Type RESTORE to confirm">
            <Input
              data-testid="restore-confirm-input"
              value={restoreConfirm}
              onChange={(e) => setRestoreConfirm(e.target.value)}
              placeholder="RESTORE"
            />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={restoreConfirm !== "RESTORE"}
              data-testid="restore-confirm-button"
              onClick={async () => {
                const b = restoreTarget.backup;
                setRestoreTarget(null);
                await run("restore", () =>
                  api.post(`/backups/${b.id}/restore`, { dry_run: false, confirm: "RESTORE" }),
                  "Database restored from backup");
              }}
            >
              Restore now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
